import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';
import type {
  DrawerProps,
  DrawerTriggerProps,
  DrawerPortalProps,
  DrawerCloseProps,
  DrawerOverlayProps,
  DrawerContentProps,
  DrawerHeaderProps,
  DrawerFooterProps,
  DrawerTitleProps,
  DrawerDescriptionProps,
} from './types';
import { cn } from '../../lib/utils';

function Drawer({ ...props }: DrawerProps) {
  return <DrawerPrimitive.Root data-slot='drawer' {...props} />;
}

const DrawerTrigger = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Trigger>,
  DrawerTriggerProps
>(({ ...props }, ref) => {
  return (
    <DrawerPrimitive.Trigger ref={ref} data-slot='drawer-trigger' {...props} />
  );
});

DrawerTrigger.displayName = DrawerPrimitive.Trigger.displayName;

function DrawerPortal({ ...props }: DrawerPortalProps) {
  return <DrawerPrimitive.Portal data-slot='drawer-portal' {...props} />;
}

const DrawerClose = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Close>,
  DrawerCloseProps
>(({ ...props }, ref) => {
  return (
    <DrawerPrimitive.Close ref={ref} data-slot='drawer-close' {...props} />
  );
});

DrawerClose.displayName = DrawerPrimitive.Close.displayName;

const DrawerOverlay = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Overlay>,
  DrawerOverlayProps
>(({ className, ...props }, ref) => {
  return (
    <DrawerPrimitive.Overlay
      ref={ref}
      data-slot='drawer-overlay'
      className={cn(
        'eye:fixed eye:inset-0 eye:z-1300 eye:bg-black/50 eye:data-[state=closed]:animate-out eye:data-[state=closed]:fade-out-0 eye:data-[state=open]:animate-in eye:data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
});

DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Content>,
  DrawerContentProps
>(({ className, children, ...props }, ref) => {
  return (
    <DrawerPortal data-slot='drawer-portal'>
      <DrawerOverlay />
      <DrawerPrimitive.Content
        ref={ref}
        data-slot='drawer-content'
        className={cn(
          'eye:group/drawer-content eye:fixed eye:z-1300 eye:flex eye:h-auto eye:flex-col eye:border-grey-20 eye:bg-white',
          'eye:data-[vaul-drawer-direction=top]:inset-x-0 eye:data-[vaul-drawer-direction=top]:top-0 eye:data-[vaul-drawer-direction=top]:mb-96 eye:data-[vaul-drawer-direction=top]:max-h-[80vh] eye:data-[vaul-drawer-direction=top]:rounded-b-md eye:data-[vaul-drawer-direction=top]:border-b',
          'eye:data-[vaul-drawer-direction=bottom]:inset-x-0 eye:data-[vaul-drawer-direction=bottom]:bottom-0 eye:data-[vaul-drawer-direction=bottom]:mt-96 eye:data-[vaul-drawer-direction=bottom]:max-h-[80vh] eye:data-[vaul-drawer-direction=bottom]:rounded-t-md eye:data-[vaul-drawer-direction=bottom]:border-t',
          'eye:data-[vaul-drawer-direction=right]:inset-y-0 eye:data-[vaul-drawer-direction=right]:right-0 eye:data-[vaul-drawer-direction=right]:w-3/4 eye:data-[vaul-drawer-direction=right]:[border-left-width:1px] eye:data-[vaul-drawer-direction=right]:[border-left-style:solid] eye:data-[vaul-drawer-direction=right]:sm:max-w-md',
          'eye:data-[vaul-drawer-direction=left]:inset-y-0 eye:data-[vaul-drawer-direction=left]:left-0 eye:data-[vaul-drawer-direction=left]:w-3/4 eye:data-[vaul-drawer-direction=left]:border-r eye:data-[vaul-drawer-direction=left]:sm:max-w-sm',
          className,
        )}
        {...props}
      >
        <div className='eye:mx-auto eye:mt-xs eye:hidden eye:h-8 eye:w-[100px] eye:shrink-0 eye:rounded-full eye:bg-grey-40 eye:group-data-[vaul-drawer-direction=bottom]/drawer-content:block' />
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
});

DrawerContent.displayName = DrawerPrimitive.Content.displayName;

function DrawerHeader({ className, ...props }: DrawerHeaderProps) {
  return (
    <div
      data-slot='drawer-header'
      className={cn(
        'eye:flex eye:flex-col eye:gap-5xs eye:p-xs eye:group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center eye:group-data-[vaul-drawer-direction=top]/drawer-content:text-center eye:md:text-left',
        className,
      )}
      {...props}
    />
  );
}

function DrawerFooter({ className, ...props }: DrawerFooterProps) {
  return (
    <div
      data-slot='drawer-footer'
      className={cn(
        'eye:mt-auto eye:flex eye:flex-col eye:gap-8 eye:p-xs',
        className,
      )}
      {...props}
    />
  );
}

const DrawerTitle = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Title>,
  DrawerTitleProps
>(({ className, ...props }, ref) => {
  return (
    <DrawerPrimitive.Title
      ref={ref}
      data-slot='drawer-title'
      className={cn(
        'eye:text-18 eye:font-semibold eye:tracking-0 eye:md:text-22',
        className,
      )}
      {...props}
    />
  );
});

DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Description>,
  DrawerDescriptionProps
>(({ className, ...props }, ref) => {
  return (
    <DrawerPrimitive.Description
      ref={ref}
      data-slot='drawer-description'
      className={cn('eye:body-s eye:text-grey-120', className)}
      {...props}
    />
  );
});

DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
